/**
 * An https origin that other people's software must be able to reach — parsed
 * once, for every setting that is one.
 *
 * Two deployment settings have exactly this shape and must refuse exactly the
 * same mistakes:
 *
 *   - `TEMPLATE_ASSET_PUBLIC_BASE_URL`, which every recipient's mail client
 *     fetches pictures from (slice 21);
 *   - `API_BASE_URL`, which every recipient's mail client posts a one-click
 *     unsubscribe to, and which Amazon's SNS posts delivery reports to
 *     (slice 19).
 *
 * Slice 21 wrote the parser by hand, inside `./template.js`, after its gate found
 * host names that were not host names booting a deployment into hosted mode.
 * Slice 19 needed a second one when it found that the unsubscribe link in every
 * real campaign pointed at `http://localhost:3001` — the fallback a missing
 * setting takes outside production, and the live deployment runs in
 * development mode. A second hand-written copy would be two lists of refusals
 * that drift the day somebody fixes one of them, so the parser moved here, out
 * of `./template.js`, and each setting supplies only the words its refusals are
 * phrased in. `templateAssetPublicBaseUrlSchema`'s own header, which still sits
 * beside that setting, is where each refusal is argued.
 *
 * A leaf: this file imports zod and nothing else, so `./template.js` — which is
 * already in a cycle with `./whatsapp.js` — gains an edge out and none back.
 *
 * Hand-parsed for the reason `templateHrefSchema` is: `@wend/shared` compiles
 * with `"types": []` and `lib: ["ES2022"]`, so there is no `URL` global to lean
 * on, and the explicit form documents the refusals anyway.
 */
import { z } from 'zod';

/**
 * Control characters that must never reach a template field or a URL.
 *
 * Not a security control — escaping at render is that — but a data-hygiene one:
 * a NUL, a vertical tab or a stray `\x1b` arrives by paste accident, survives
 * into compiled HTML or a header, and then shows up as a mojibake box in
 * somebody's inbox. `\n`, `\r` and `\t` are deliberately absent from the class;
 * the single-line fields refuse newlines separately. Here rather than in
 * `./template.js` so this leaf needs nothing from it.
 */
export const CONTROL_CHARACTERS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;

/**
 * One DNS label: a letter or digit at each end, hyphens allowed inside, at most
 * 63 characters; labels joined by single dots. Refuses `_`, `%`, an empty label
 * (`a..b`) and a label that starts or ends with a hyphen. A dotted IPv4 literal
 * matches it too, which is intended: a host fronted by a bare address is odd
 * but reachable, and loopback is refused separately and by name.
 */
const HOST_NAME_PATTERN =
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;

/**
 * The words one setting's refusals are phrased in. Every refusal starts with
 * `subject`, and each "why" finishes the sentence that names its mistake — so a
 * person who typed `http://` reads why *this* address has to be https, not a
 * generic rule.
 */
export interface PublicBaseUrlWords {
  /** How the refusals name the value — "The image host", "The API base URL". */
  readonly subject: string;
  /** A well-formed value to copy the shape of — "https://img.example.in". */
  readonly example: string;
  /** Finishes "… must use https, not http — ". */
  readonly whyHttps: string;
  /** Finishes "… must not carry credentials — ". */
  readonly whyNoCredentials: string;
  /** Finishes "… \"localhost\" is this machine — ". */
  readonly whyNotLoopback: string;
}

/**
 * The schema for one such setting: https, a real host that is not this machine,
 * an optional path, and no trailing slash, query, fragment or credentials.
 */
export function publicHttpsBaseUrlSchema(words: PublicBaseUrlWords) {
  const { subject, example } = words;
  return z
    .string()
    .trim()
    .min(1)
    .superRefine((value, ctx) => {
      const fail = (message: string): void => {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message });
      };

      if (/[\s]/.test(value) || CONTROL_CHARACTERS.test(value)) {
        fail(`${subject} cannot contain spaces or control characters`);
        return;
      }
      if (value.includes('?') || value.includes('#')) {
        fail(`${subject} must not carry a query string or a fragment`);
        return;
      }
      if (value.endsWith('/')) {
        fail(`${subject} must not end with a slash, e.g. ${example}`);
        return;
      }

      const match = /^([A-Za-z][A-Za-z0-9+.-]*):\/\/([^/]+)(\/.*)?$/.exec(value);
      if (match === null) {
        fail(`${subject} must be an absolute URL, e.g. ${example}`);
        return;
      }

      const scheme = (match[1] as string).toLowerCase();
      const authority = match[2] as string;

      // The scheme first, and its own sentence: "not https" is the refusal an
      // operator who typed `http://` needs to read, and it must not be reported as
      // "not an absolute URL", which would send them looking for the wrong mistake.
      if (scheme !== 'https') {
        fail(`${subject} must use https, not ${scheme} — ${words.whyHttps}`);
        return;
      }
      if (authority.includes('@')) {
        fail(`${subject} must not carry credentials — ${words.whyNoCredentials}`);
        return;
      }

      /*
       * THE AUTHORITY ITSELF. Everything above is shape around the host; nothing
       * above looks at the host. `https://%%%`, `https://:443` and
       * `https://img.example.in:99999` all passed slice 21's first version and
       * booted a deployment with an address no client can even resolve.
       */
      const bracketed = /^\[([0-9A-Fa-f:.]+)\](?::([^:]*))?$/.exec(authority);
      const named = bracketed === null ? /^([^:[\]]+)(?::([^:]*))?$/.exec(authority) : null;
      const hostMatch = bracketed ?? named;
      if (hostMatch === null) {
        fail(`${subject} has no usable host name in "${authority}" — it must look like ${example}`);
        return;
      }
      const host = (hostMatch[1] as string).toLowerCase();
      const port = hostMatch[2];

      if (port !== undefined && !(/^\d{1,5}$/.test(port) && Number(port) >= 1 && Number(port) <= 65535)) {
        fail(`${subject}'s port "${port}" is not a port — it must be a number from 1 to 65535, or left out`);
        return;
      }
      if (named !== null && !(host.length <= 253 && HOST_NAME_PATTERN.test(host))) {
        fail(
          `${subject} "${host}" is not a valid host name — letters, digits and hyphens in dot-separated labels, with an internationalised name written in its xn-- form`,
        );
        return;
      }

      /*
       * Loopback, refused by NAME as well as by scheme: an address only this
       * machine can reach is an address nobody else can, and that argument does
       * not care whether the scheme was right. Slice 21's first version checked
       * only the scheme, which left `https://localhost` booting.
       */
      if (
        host === 'localhost' ||
        host.endsWith('.localhost') ||
        /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host) ||
        host === '0.0.0.0' ||
        (bracketed !== null && (host === '::1' || host === '::'))
      ) {
        fail(`${subject} "${host}" is this machine — ${words.whyNotLoopback}`);
      }
    });
}

/**
 * The env var that carries the API's public origin — the config refusal names it
 * through this constant. Several sentences elsewhere (the boot warning, the token
 * services, the setup copy) still type it out, so a rename is a search, not an
 * edit here.
 */
export const API_BASE_URL_ENV = 'API_BASE_URL';

/**
 * `API_BASE_URL` — the public https origin that reaches this API, e.g.
 * `https://api.example.in`, or `https://example.in/wend` behind a path.
 *
 * **Two things strangers use are built from it, and both were broken without
 * it.** Every campaign email carries `List-Unsubscribe: <…/api/u/<token>>` and,
 * where the template asks for one, a footer link to the same address; and the
 * webhook URL a tenant subscribes their SNS topic to is `…/api/webhooks/ses/…`.
 * With nothing set, outside production both fall back to
 * `http://localhost:<PORT>` — which is right for a developer clicking their own
 * test mail and wrong for everybody else — and the live deployment runs in
 * development mode. So slice 19 found the unsubscribe link in every real email
 * sent up to 17 Sep 2026 — 2,888 of them — pointing at a machine no recipient
 * could reach, and a webhook URL no SNS topic could deliver to. Production refuses to boot without it; development warns on
 * every boot while it sends real mail without it.
 */
export const apiPublicBaseUrlSchema = publicHttpsBaseUrlSchema({
  subject: 'The API base URL',
  example: 'https://api.example.in',
  whyHttps:
    'mail providers only honour a one-click unsubscribe sent over https, and Amazon’s delivery reports name the recipients they are about',
  whyNoCredentials: 'it is written into every message Wend sends',
  whyNotLoopback:
    'no recipient can reach the unsubscribe links built from it, and Amazon cannot deliver reports to it',
});
